import { useMemo } from "react";
import { Row, Col, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getAsset } from "../../../../helpers/helper";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import dummyUser from "@/assets/images/dummy/user-avatar-img.jpg";
import { useGetAllConnectedQuery } from "../../../../features/connection/connectionApiSlice";
import { encodeId } from "../../../../utils/idObfuscator";
import "./Rawmaterialcard.css";

const businessTypeRoutes = {
  homemadeShop: "/Home-Made-Item-Userprofile/",
  gemShop: "/Minerl-And-Mining-UserProfile/",
  services: "/services-online/",
  usedShop: "/usedItems-userprofile/",
  rawmaterialShop: "/rawmaterial-userprofile/",
  manufacturerShop: "/manufacturer-userprofile/",
  wholesellerShop: "/wholeseller-userprofile/",
  retailerShop: "/retailer-userprofile/",
  recycledShop: "/Recycleuserprofile/",
  farmingShop: "/Farminguserprofile/",
  usedVehicleShop: "/Byvehicleuserprofile/",
  transportShop: "/buying-transport/",
  businessPropertyCompany: "/user-peronal-property/",
  foodShop: "/food-buying-profile/",
  company: "/find-jobs-online/",
  jobsCompany: "/find-jobs-online/",
  generalPublic: "/other-profile/",
  marketerProfile: "/view-marketer-profile/",
  "Approved Marketers": "/view-marketer-profile/",
};

const createSixDummyItems = (label = "Shop Name") =>
  Array.from({ length: 6 }, () => ({ shopname: label }));

const dedupeItems = (items) => {
  if (!Array.isArray(items) || items.length === 0) return [];
  const seen = new Set();
  const unique = [];
  for (const item of items) {
    const key = [
      item?.userid?._id?.toString?.() ||
        item?.userid?.toString?.() ||
        item?.userid ||
        "",
      item?.shopid?._id?.toString?.() ||
        item?.shopid?.toString?.() ||
        item?.shopid ||
        "null",
      item?._marketerDocId?.toString?.() || item?._marketerDocId || "",
    ].join("|");
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(item);
  }
  return unique;
};

const normalizeItemsToSix = (items, fallbackLabel = "Shop Name") => {
  const unique = dedupeItems(items);
  if (unique.length === 0) return createSixDummyItems(fallbackLabel);
  const mapped = unique.slice(0, 6).map((it) => ({ ...it }));
  while (mapped.length < 6) mapped.push({ shopname: fallbackLabel });
  return mapped;
};

const Rawmaterialcard = ({
  id,
  profileurl,
  setProfileshowhide,
  profileLayout = true,
}) => {
  const navigate = useNavigate();
  const { name } = useSelector((state) => state.user);

  const { data: allConnectedData } = useGetAllConnectedQuery(
    { shopid: id, userid: name?._id },
    {
      skip: !id || !name?._id,
      refetchOnMountOrArgChange: true,
    },
  );

  const topdata = useMemo(() => {
    const payload = allConnectedData?.connectedBT
      ? allConnectedData
      : allConnectedData?.data || allConnectedData || {};
    let connectedBT = Array.isArray(payload?.connectedBT)
      ? [...payload.connectedBT]
      : [];

    // Always merge person-level connections from flat `connected` when the
    // matching Connectivity group is missing OR has no real tiles.
    // Note: getAllConnected?shopid=… already scopes `connected` to this shop,
    // so we only need "I am a party + other side is a person".
    const realCount = (g) =>
      (g?.items || []).filter(
        (it) => it && !it.isPlaceholder && (it.userid || it.shopid || it._id),
      ).length;
    const gpGroup = connectedBT.find((g) => g?.businessType === "generalPublic");
    const mkGroupExisting = connectedBT.find((g) => {
      const n = String(g?.businessType || "")
        .toLowerCase()
        .replace(/[\s_-]+/g, "");
      return (
        n === "marketerprofile" ||
        n === "marketer" ||
        n === "marketershop" ||
        n === "approvedmarketers"
      );
    });
    const needGpFallback = !gpGroup || realCount(gpGroup) === 0;
    const needMkFallback = !mkGroupExisting || realCount(mkGroupExisting) === 0;

    if (
      (needGpFallback || needMkFallback) &&
      Array.isArray(payload?.connected) &&
      id
    ) {
      const me = String(name?._id || "");
      const gpItems = [];
      const marketerItems = [];
      const seenGp = new Set();
      const seenMk = new Set();
      const isMarketerRow = (row) => {
        if (
          row?.asMarketerProfile === true ||
          row?.asMarketerProfile === "true" ||
          row?.fromMarketerProfile === true ||
          row?.fromMarketerProfile === "true"
        ) {
          return true;
        }
        const nSend = String(row?.senderBusinessType || "")
          .toLowerCase()
          .replace(/[\s_-]+/g, "");
        const nRecv = String(row?.businessType || "")
          .toLowerCase()
          .replace(/[\s_-]+/g, "");
        return (
          nSend === "marketerprofile" ||
          nSend === "marketer" ||
          nSend === "marketershop" ||
          nRecv === "marketerprofile" ||
          nRecv === "marketer" ||
          nRecv === "marketershop"
        );
      };
      payload.connected.forEach((row) => {
        const senderShop = row?.sendershopid
          ? String(row.sendershopid._id || row.sendershopid)
          : "";
        const receiverShop = row?.recievershopid
          ? String(row.recievershopid._id || row.recievershopid)
          : "";
        const iAmSender = String(row?.senderid?._id || row?.senderid) === me;
        const iAmReceiver =
          String(row?.recieverid?._id || row?.recieverid) === me;
        if (!iAmSender && !iAmReceiver) return;

        // Other party is a person (no shop on their side) = GP / marketer tile
        const otherIsPerson = iAmSender ? !receiverShop : !senderShop;
        if (!otherIsPerson && !isMarketerRow(row)) return;

        const otherUser = iAmSender
          ? row?.receiverUser || row?.recieverid
          : row?.senderUser || row?.senderid;
        const otherId =
          otherUser?._id ||
          otherUser ||
          (iAmSender ? row?.recieverid : row?.senderid);
        const uid = String(otherId?._id || otherId || "");
        if (!uid) return;

        const tile = {
          userid: uid,
          shopid: null,
          shopname:
            otherUser?.fullname ||
            otherUser?.shopname ||
            row?.receiverUser?.fullname ||
            row?.senderUser?.fullname ||
            "Profile",
          image:
            otherUser?.imageUrl ||
            otherUser?.image ||
            row?.receiverUser?.imageUrl ||
            row?.senderUser?.imageUrl ||
            "N/A",
        };

        if (isMarketerRow(row)) {
          if (!needMkFallback || seenMk.has(uid)) return;
          seenMk.add(uid);
          marketerItems.push({
            ...tile,
            businessType: "marketerProfile",
            _marketerDocId: row?._marketerDocId || null,
          });
        } else if (otherIsPerson) {
          if (!needGpFallback || seenGp.has(uid)) return;
          seenGp.add(uid);
          gpItems.push(tile);
        }
      });
      if (needGpFallback && gpItems.length > 0) {
        const withoutGp = connectedBT.filter(
          (g) => g?.businessType !== "generalPublic",
        );
        connectedBT = [
          {
            businessType: "generalPublic",
            count: gpItems.length,
            items: gpItems,
          },
          ...withoutGp,
        ];
      }
      if (needMkFallback && marketerItems.length > 0) {
        connectedBT = [
          {
            businessType: "marketerProfile",
            count: marketerItems.length,
            items: marketerItems,
          },
          ...connectedBT.filter((g) => {
            const n = String(g?.businessType || "")
              .toLowerCase()
              .replace(/[\s_-]+/g, "");
            return (
              n !== "marketerprofile" &&
              n !== "marketer" &&
              n !== "marketershop" &&
              n !== "approvedmarketers"
            );
          }),
        ];
      }
    }

    // Reclassify: marketing-origin person tiles must not stay under General Public
    // (accepted marketer→shop connects were incorrectly landing there).
    // Re-find after fallback merge — connectedBT may have been replaced above.
    const gpGroupForReclass = connectedBT.find(
      (g) => g?.businessType === "generalPublic",
    );
    if (
      gpGroupForReclass &&
      Array.isArray(gpGroupForReclass.items) &&
      gpGroupForReclass.items.length > 0
    ) {
      const stayGp = [];
      const moveMk = [];
      for (const it of gpGroupForReclass.items) {
        if (
          it?._marketerDocId ||
          it?.asMarketerProfile === true ||
          it?.businessType === "marketerProfile" ||
          String(it?.linkBusinessType || "")
            .toLowerCase()
            .includes("marketer")
        ) {
          moveMk.push({ ...it, businessType: "marketerProfile" });
        } else {
          stayGp.push(it);
        }
      }
      if (moveMk.length > 0) {
        gpGroupForReclass.items = stayGp;
        gpGroupForReclass.count = stayGp.length;
        let mkGroup = connectedBT.find((g) => {
          const n = String(g?.businessType || "")
            .toLowerCase()
            .replace(/[\s_-]+/g, "");
          return n === "marketerprofile" || n === "approvedmarketers";
        });
        if (!mkGroup) {
          mkGroup = {
            businessType: "marketerProfile",
            count: 0,
            items: [],
          };
          connectedBT.push(mkGroup);
        }
        const seen = new Set(
          (mkGroup.items || []).map(
            (x) =>
              String(x?.userid?._id || x?.userid || "") +
              "|" +
              String(x?.shopid || "null"),
          ),
        );
        for (const it of moveMk) {
          const key =
            String(it?.userid?._id || it?.userid || "") +
            "|" +
            String(it?.shopid || "null");
          if (seen.has(key)) continue;
          seen.add(key);
          mkGroup.items = mkGroup.items || [];
          mkGroup.items.push(it);
        }
        mkGroup.count = (mkGroup.items || []).length;
      }
    }

    const updated = [];

    connectedBT.forEach((apiData) => {
      const isGeneralPublic = apiData.businessType === "generalPublic";
      const uniqueItems = dedupeItems(apiData.items);
      // A group with nothing in it is a shell, not a connection. Rendering it
      // gave new shops a named "General Public — 0" card; leave the slot to the
      // blank placeholders below instead.
      if (uniqueItems.length === 0 && !(Number(apiData.count) > 0)) return;
      updated.push({
        businessType: apiData.businessType || "Business Name",
        count: uniqueItems.length || apiData.count || 0,
        items: normalizeItemsToSix(
          uniqueItems,
          isGeneralPublic ? "Profile Name" : "Shop Name",
        ),
        isPlaceholder: false,
      });
    });

    // Keep General Public connectivity card near the front when present
    const gpIdx = updated.findIndex((c) => c.businessType === "generalPublic");
    if (gpIdx > 0) {
      const [gpCard] = updated.splice(gpIdx, 1);
      updated.unshift(gpCard);
    }

    // Keep Marketer Profile near the front (after General Public when both exist)
    const mkIdx = updated.findIndex((c) => {
      const n = String(c.businessType || "")
        .toLowerCase()
        .replace(/[\s_-]+/g, "");
      return n === "marketerprofile" || n === "approvedmarketers";
    });
    if (mkIdx > 0) {
      const [mkCard] = updated.splice(mkIdx, 1);
      const gpIdx2 = updated.findIndex((c) => c.businessType === "generalPublic");
      if (gpIdx2 === 0) {
        updated.splice(1, 0, mkCard);
      } else {
        updated.unshift(mkCard);
      }
    }

    const remaining = 12 - updated.length;
    for (let i = 0; i < remaining; i++) {
      updated.push({
        businessType: "Business Name",
        count: 0,
        items: createSixDummyItems("Shop Name"),
        isPlaceholder: true,
      });
    }

    return updated;
  }, [allConnectedData, id, name?._id]);

  const formatBusinessTypeTitle = (bt) => {
    if (!bt) return "";
    if (bt === "generalPublic") return "General Public";
    if (bt === "marketerProfile" || bt === "Approved Marketers")
      return "Marketer Profile";
    if (bt === "businessPropertyCompany") return "Business Property";
    if (bt === "Business Name") return "Business Name";
    // Convert camelCase to Title Case (e.g., homemadeShop -> Homemade Shop)
    const result = bt.replace(/([A-Z])/g, " $1");
    return result.charAt(0).toUpperCase() + result.slice(1);
  };

  return (
    <div
      className={`connected-businesses${profileLayout ? " connected-businesses-profile-layout" : ""}`}
    >
      <Row className="connected-businesses-row ps-2 pe-2">
        {topdata.map((card, index) => (
          <Col xl="4" lg="6" md="6" sm="6" xs="6" key={index}>
            <Card className="connected-businesses-card mt-2 custom-styling-card fade-in">
              <Card.Body className="p-2 pt-1 pb-1">
                <div className="connected-businesses-card-row row pt-3">
                  <Col lg="8" md="8" xs="8">
                    <p className="font_13 text-left mb-0">
                      {formatBusinessTypeTitle(card.businessType).slice(0, 22)}
                    </p>
                    <p className="font_11 text-left">
                      {card.count}{" "}
                      {formatBusinessTypeTitle(card.businessType).slice(0, 22)}
                    </p>
                  </Col>

                  <Col lg="4" md="4" xs="4">
                    <p
                      className={`connected-businesses-view-all font_11 text-right ${card.count > 0 ? "cursor_P" : ""}`}
                      style={{
                        color: card.count > 0 ? "#0353A4" : "#a0a0a0",
                        fontWeight: "700",
                        cursor: card.count > 0 ? "pointer" : "not-allowed",
                      }}
                      onClick={async () => {
                        if (card.count > 0) {
                          await setProfileshowhide(card.businessType);
                        }
                      }}
                    >
                      View All
                    </p>
                  </Col>

                  {card.items.map((item, entityIndex) => {
                    const canView = item?.shopid || item?.userid;

                    return (
                      <div
                        className="connected-businesses-item col-4 ps-2 pe-2 text-center"
                        key={`${index}-${entityIndex}`}
                      >
                        <div
                          className="connected-businesses-avatar ms-auto me-auto d-flex justify-content-center align-items-center"
                          style={{
                            borderRadius: "50%",
                            overflow: "hidden",
                          }}
                        >
                          {canView ? (
                            <img
                              src={(() => {
                                const imgSource = item?.image || item?.imageUrl;
                                if (
                                  !imgSource ||
                                  imgSource === "N/A" ||
                                  imgSource.length === 0
                                )
                                  return dummyUser;
                                const finalSrc = Array.isArray(imgSource)
                                  ? imgSource[0]
                                  : imgSource;
                                return finalSrc
                                  ? getAsset(finalSrc)
                                  : dummyUser;
                              })()}
                              alt={item?.shopname || item?.fullname || ""}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                              crossOrigin="anonymous"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = dummyUser;
                              }}
                            />
                          ) : (
                            <AccountCircleIcon
                              style={{
                                width: "100%",
                                height: "100%",
                                color: "#0353A4",
                              }}
                            />
                          )}
                        </div>

                        <div className="font_exsmall mt-1">
                          <p className="text-center mb-1">
                            {(item?.shopname || item?.fullname || "")
                              .slice(0, 6)
                              .concat(
                                (item?.shopname || item?.fullname || "")
                                  .length > 6
                                  ? "..."
                                  : "",
                              )}
                          </p>

                          <button
                            className="connected-businesses-view-button follow-connt text-center mb-2"
                            style={{
                              backgroundColor: "#0353A4",
                              color: "white",
                              border: "none",
                              padding: "1px 10px",
                              borderRadius: "15px",
                              cursor: canView ? "pointer" : "not-allowed",
                              opacity: canView ? 1 : 0.6,
                            }}
                            disabled={!canView}
                            onClick={() => {
                              if (!canView) return;

                              const routeBT =
                                item?.shopid && item?.linkBusinessType
                                  ? item.linkBusinessType
                                  : card.businessType;
                              const route =
                                businessTypeRoutes[routeBT] ||
                                businessTypeRoutes[card.businessType];
                              const viewId =
                                item?.shopid?._id ||
                                item?.shopid ||
                                item?.userid?._id ||
                                item?.userid ||
                                item?._id;

                              if (route && viewId) {
                                const finalId =
                                  routeBT === "marketerProfile" ||
                                  routeBT === "Approved Marketers"
                                    ? encodeId(viewId)
                                    : viewId;
                                navigate(`${route}${finalId}`);
                              }
                            }}
                          >
                            View
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default Rawmaterialcard;
